import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons'
import type { CsvExportParams, GridApi } from 'ag-grid-community'
import { format } from 'date-fns'
import { useIntl } from 'react-intl'

import { SpinnerDots } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import type { ListingSearchState } from '@/components/listings/search/ListingSearchProvider'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { FiltersButton, FiltersIcon } from '../shared/listings-ui'
import { MAX_MANIFEST_EXPORT_LISTINGS } from './data-loading/fetchAllFilteredListingIds'
import { getAllExportColumnKeys } from './helpers/getCsvExportColumnKeys'
import { trackExportEvent } from './helpers/listViewAnalytics'
import type { ListViewSurface } from './helpers/listViewSurface'
import { useDownloadAllManifests } from './useDownloadAllManifests'

const defaultUrlPrefix =
  typeof window !== 'undefined' ? window.location.origin : 'https://bstock.com'

const getFileName = () =>
  `B-Stock listings ${format(new Date(), "yyyy-MM-dd'T'HHmm")}.csv`

export const defaultCsvExportParams: CsvExportParams = {
  fileName: getFileName,
  processHeaderCallback({ column }) {
    const { headerName } = column.getColDef()
    if (headerName === 'Closes In') return 'End Date'
    return headerName ?? ''
  },
}

/**
 * Set `includeManifests=true` to export manifest data (item-level SKU rows)
 * for every listing matching the current filters — across all pages, not
 * just what's currently loaded in the grid. Works both for a single
 * storefront (Storefront List View, gated behind ENABLE_ALP_MANIFEST_EXPORT
 * plus that storefront's own setting) and without one (ALP, always on).
 *
 * Otherwise, this export button gets all columns and all rows currently in
 * the grid. (Right-click export gets visible columns and visible rows.)
 */
export const ExportListViewButton = ({
  gridApi,
  includeManifests,
  resultCount,
  state,
  storefront,
  surface,
}: {
  gridApi: GridApi<CombinedListingSearchResult> | null
  includeManifests?: boolean
  resultCount?: number
  state: ListingSearchState
  storefront?: StoreFrontDto
  surface?: ListViewSurface
}) => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked, ...analytics } = useAnalytics()

  const enableManifestExport = !!includeManifests

  // Always called (rules of hooks) — only actually used when
  // enableManifestExport is true. `storefront` is optional here: Storefront
  // List View passes it, ALP doesn't — the hook handles both. urlPrefix is
  // always the bare origin — order-process appends /buy/listings/details/
  // to it directly, and storefront context travels separately via
  // storefrontSlug, so prefixing it here would double up the path.
  const { downloadAllManifests, isPending } = useDownloadAllManifests({
    state,
    storefront,
    surface,
    urlPrefix: defaultUrlPrefix,
  })

  const onClick = () => {
    if (enableManifestExport) {
      void downloadAllManifests()
      return
    }

    if (!gridApi) return

    trackExportEvent({ trackButtonClicked }, analytics, 'button', surface)
    gridApi.exportDataAsCsv({
      ...defaultCsvExportParams,
      columnKeys: getAllExportColumnKeys(gridApi),
      exportedRows: 'all',
    })
  }

  // The plain CSV path only has whatever's currently loaded in the grid to
  // export, so it stays keyed off the grid's row count. The manifest path
  // exports the full filtered set independent of the grid, so it's keyed off
  // the live filtered total instead — a page mid-transition can show zero
  // grid rows while the filtered set is still very much nonempty.
  const isDisabled = enableManifestExport
    ? !resultCount || isPending
    : !gridApi?.getDisplayedRowCount()

  const title = enableManifestExport
    ? formatMessage(
        { id: 'Listings.ListView.export.manifestButtonText' },
        { count: resultCount ?? 0, cap: MAX_MANIFEST_EXPORT_LISTINGS }
      )
    : formatMessage({ id: 'Listings.ListView.export.buttonText' })

  return (
    <FiltersButton
      appearance="secondary"
      aria-busy={isPending}
      disabled={isDisabled}
      onClick={onClick}
      title={title}
    >
      {isPending ? <SpinnerDots /> : <FiltersIcon icon={faFileArrowDown} />}
    </FiltersButton>
  )
}
